import * as Babel from "@babel/standalone";
import * as React from "react";
import { validateDashboardRuntimeSource } from "./dashboard-runtime.validation";

export type RuntimeModules = Record<string, Record<string, unknown>>;

type CompileResult =
	| {
			component: React.ComponentType;
			error: null;
	  }
	| {
			component: null;
			error: string;
	  };

const buildFactory = (compiledCode: string, modules: RuntimeModules) => {
	const moduleMap: Record<string, Record<string, unknown>> = {
		...modules,
		react: { ...modules.react, default: React },
	};

	const require = (specifier: string) => {
		const mod = moduleMap[specifier];
		if (!mod) {
			throw new Error(`Module "${specifier}" is not available in the dashboard runtime.`);
		}
		return mod;
	};

	const moduleObj = { exports: {} as Record<string, unknown> };
	const body = `${compiledCode}\nreturn module.exports.default ?? exports.default;`;

	const evaluator = Function("React", "require", "module", "exports", body);
	const resolved = evaluator(React, require, moduleObj, moduleObj.exports);

	if (typeof resolved !== "function") {
		throw new Error(
			`Runtime JSX must default export a React component. (received ${typeof resolved})`,
		);
	}

	return resolved as React.ComponentType;
};

const normalizeSource = (raw: string): string => {
	let source = raw;

	if (/from\s+\\"/.test(source) || /import\s+\\"/.test(source)) {
		source = source.replace(/\\"/g, '"').replace(/\\n/g, "\n").replace(/\\t/g, "\t");
	}

	if (!source.includes("\n")) {
		source = source.replace(
			/\/\/[^"'`]*?(?=\b(?:const|let|var|function|class|export|import|return|if|for|while|type|interface)\b)/g,
			"",
		);
	}

	return source;
};

export const compileDashboardRuntime = (
	source: string,
	modules: RuntimeModules,
): CompileResult => {
	const normalized = normalizeSource(source);
	const validation = validateDashboardRuntimeSource(normalized);
	if (!validation.ok) {
		return {
			component: null,
			error: validation.issues.map((issue) => issue.message).join(" "),
		};
	}

	try {
		const stripped = Babel.transform(normalized, {
			filename: "workflow-dashboard-runtime.tsx",
			presets: [
				["react", { runtime: "classic" }],
				["typescript", { onlyRemoveTypeImports: true }],
			],
			sourceType: "module",
		});

		if (!stripped.code) {
			return {
				component: null,
				error: "Runtime JSX compilation returned no executable code.",
			};
		}

		const result = Babel.transform(stripped.code, {
			filename: "workflow-dashboard-runtime.js",
			plugins: ["transform-modules-commonjs"],
			sourceType: "module",
		});

		if (!result.code) {
			return {
				component: null,
				error: "Runtime JSX compilation returned no executable code.",
			};
		}

		return {
			component: buildFactory(result.code, modules),
			error: null,
		};
	} catch (error) {
		return {
			component: null,
			error:
				error instanceof Error
					? error.message
					: "Runtime JSX compilation failed for an unknown reason.",
		};
	}
};
